import { useCallback, useEffect, useState } from 'react'

import { ContainerImportPlan } from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import {
    ImportStep,
    mapContainerErrorsToRows,
    mapSpreadsheetErrorsToRows,
    ParsedSpreadsheet,
    plansByContainerName,
    RowValidation,
    validateContainers,
    validationGroups,
} from 'src/pages/import-content/ImportContentModel'
import { ItemPoolService } from 'src/services/item-pools/ItemPoolService'

export interface RowValidationState {
    rowValidation?: RowValidation
    // What the import does to each Item Container module, keyed by lower-cased container name.
    plansByContainer: Record<string, ContainerImportPlan>
    isValidatingRows: boolean
    validatedRowCount: number
}

interface ValidationResult {
    validated: RowValidation
    plans: Record<string, ContainerImportPlan>
}

const NOT_VALIDATED: RowValidationState = {
    rowValidation: undefined,
    plansByContainer: {},
    isValidatingRows: false,
    validatedRowCount: 0,
}

// Without containers a file can be any size, so its rows are validated in batches.
const validateInBatches = async (
    spreadsheet: ParsedSpreadsheet,
    templateModuleVersionId: string,
    onProgress: (completed: number) => void
): Promise<ValidationResult> => ({
    validated: await validateContainers(
        validationGroups(spreadsheet, true),
        async (rowIndices) =>
            mapContainerErrorsToRows(
                await ItemPoolService.validateImportedContainer(
                    templateModuleVersionId,
                    rowIndices.map((rowIndex) => spreadsheet.rows[rowIndex]),
                    false
                ),
                rowIndices
            ),
        onProgress
    ),
    plans: {},
})

// An import that creates containers is bounded (MAX_CONTAINERS_PER_IMPORT), so the whole file is
// validated by a dry run of the import, which also reports what it does to each module.
const validateByDryRun = async (
    file: File,
    spreadsheet: ParsedSpreadsheet,
    templateModuleVersionId: string,
    uploadSpreadsheet: (file: File) => Promise<string>
): Promise<ValidationResult> => {
    try {
        const { errors, containers } = await ItemPoolService.validateImportedItemPool({
            templateModuleVersionId,
            spreadsheetS3Path: await uploadSpreadsheet(file),
            createItemContainers: true,
        })
        return {
            validated: mapSpreadsheetErrorsToRows(errors, spreadsheet.rowCount),
            plans: plansByContainerName(containers),
        }
    } catch (e) {
        // The file as a whole could not be checked, which is not a problem with any one row.
        const message = e instanceof Error ? e.message : 'These rows could not be validated.'
        return { validated: { byRow: {}, general: [message] }, plans: {} }
    }
}

// Validates the spreadsheet's rows whenever the Review step shows a file, ignoring a run's results
// once a newer file, step or skip choice has superseded it.
export const useRowValidation = ({
    step,
    spreadsheet,
    file,
    skipContainerCreation,
    templateModuleVersionId,
    uploadSpreadsheet,
}: {
    step: ImportStep
    spreadsheet?: ParsedSpreadsheet
    file?: File
    skipContainerCreation: boolean
    templateModuleVersionId: string
    uploadSpreadsheet: (file: File) => Promise<string>
}): RowValidationState & { resetRowValidation: () => void } => {
    const [state, setState] = useState(NOT_VALIDATED)

    useEffect(() => {
        if (step !== ImportStep.Review || !spreadsheet || !file) {
            return
        }
        let isCurrent = true
        setState({ ...NOT_VALIDATED, isValidatingRows: true })

        const validation = skipContainerCreation
            ? validateInBatches(spreadsheet, templateModuleVersionId, (completed) => {
                  if (isCurrent) {
                      setState((previous) => ({ ...previous, validatedRowCount: completed }))
                  }
              })
            : validateByDryRun(file, spreadsheet, templateModuleVersionId, uploadSpreadsheet)
        void validation.then(({ validated, plans }) => {
            if (isCurrent) {
                setState({
                    rowValidation: validated,
                    plansByContainer: plans,
                    isValidatingRows: false,
                    validatedRowCount: spreadsheet.rowCount,
                })
            }
        })
        return () => {
            isCurrent = false
        }
    }, [step, spreadsheet, file, skipContainerCreation, templateModuleVersionId, uploadSpreadsheet])

    const resetRowValidation = useCallback(() => setState(NOT_VALIDATED), [])

    return { ...state, resetRowValidation }
}
